import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Users, Package, DollarSign, TrendingUp, Search, ShieldCheck } from 'lucide-react';

const ADMIN_EMAIL = 'rmantheikc@gmail.com';

export default function AdminPage() {
  const { user, api, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [usersTotal, setUsersTotal] = useState(0);
  const [chartType, setChartType] = useState('line');

  // Check admin access
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
        return;
      }
      if (user.email?.toLowerCase() !== ADMIN_EMAIL.toLowerCase()) {
        navigate('/404');
        return;
      }
    }
  }, [user, authLoading, navigate]);

  // Fetch admin data
  useEffect(() => {
    const fetchAdminData = async () => {
      if (!user || user.email?.toLowerCase() !== ADMIN_EMAIL.toLowerCase()) return;
      
      try {
        setLoading(true);
        
        // Fetch stats and users in parallel
        const [statsRes, usersRes] = await Promise.all([
          api().get('/admin/stats'),
          api().get('/admin/users', { params: { limit: 50 } })
        ]);
        
        setStats(statsRes.data);
        setUsers(usersRes.data.users);
        setUsersTotal(usersRes.data.total);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
        if (error.response?.status === 404) {
          navigate('/404');
        } else {
          toast.error('Failed to load admin dashboard');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [user, api, navigate]);

  // Search users
  const handleSearch = async () => {
    try {
      const params = { limit: 50 };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const res = await api().get('/admin/users', { params });
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  // Handle search on Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Get status badge variant
  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return <Badge data-testid="status-badge-active" className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>;
      case 'Creator':
        return <Badge data-testid="status-badge-creator" className="bg-purple-500/20 text-purple-400 border-purple-500/30">Creator</Badge>;
      case 'Expired':
        return <Badge data-testid="status-badge-expired" className="bg-red-500/20 text-red-400 border-red-500/30">Expired</Badge>;
      default:
        return <Badge data-testid="status-badge-free" className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30">Free</Badge>;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      </div>
    );
  }

  if (!user || user.email?.toLowerCase() !== ADMIN_EMAIL.toLowerCase()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="admin-dashboard">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="h-8 w-8 text-amber-500" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white" data-testid="admin-title">Admin Dashboard</h1>
            <p className="text-zinc-400 text-sm">Owner: Ron Manthei</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* MRR Card */}
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="mrr-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Monthly Revenue (MRR)</CardTitle>
              <DollarSign className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white" data-testid="mrr-value">
                {stats ? formatCurrency(stats.mrr) : '$0'}
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                {stats?.active_members || 0} active × $20/mo
              </p>
            </CardContent>
          </Card>

          {/* Active Members Card */}
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="members-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Active Members</CardTitle>
              <Users className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white" data-testid="members-value">
                {stats?.active_members || 0}
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                of {stats?.total_users || 0} total users
              </p>
            </CardContent>
          </Card>

          {/* Active Listings Card */}
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="active-listings-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Active Listings</CardTitle>
              <Package className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white" data-testid="active-listings-value">
                {stats?.active_listings || 0}
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Cards available for sale
              </p>
            </CardContent>
          </Card>

          {/* Sold Listings Card */}
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="sold-listings-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Sold Listings</CardTitle>
              <TrendingUp className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white" data-testid="sold-listings-value">
                {stats?.sold_listings || 0}
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Cards sold through marketplace
              </p>
            </CardContent>
          </Card>
        </div>

        {/* MRR Chart */}
        <Card className="bg-zinc-900/50 border-zinc-800 mb-8" data-testid="mrr-chart-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white">Revenue Growth</CardTitle>
            <div className="flex gap-2">
              <Button
                variant={chartType === 'line' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChartType('line')}
                className={chartType === 'line' ? 'bg-amber-500 hover:bg-amber-600' : 'border-zinc-700 text-zinc-400'}
                data-testid="chart-type-line"
              >
                Line
              </Button>
              <Button
                variant={chartType === 'bar' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChartType('bar')}
                className={chartType === 'bar' ? 'bg-amber-500 hover:bg-amber-600' : 'border-zinc-700 text-zinc-400'}
                data-testid="chart-type-bar"
              >
                Bar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full" data-testid="mrr-chart">
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'line' ? (
                  <LineChart data={stats?.mrr_trends || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                    <YAxis stroke="#71717a" fontSize={12} tickFormatter={(value) => `$${value}`} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#18181b',
                        border: '1px solid #27272a',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value) => [`$${value}`, 'MRR']}
                    />
                    <Line
                      type="monotone"
                      dataKey="mrr"
                      stroke="#f59e0b"
                      strokeWidth={3}
                      dot={{ fill: '#f59e0b', strokeWidth: 2 }}
                      activeDot={{ r: 6, fill: '#f59e0b' }}
                    />
                  </LineChart>
                ) : (
                  <BarChart data={stats?.mrr_trends || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                    <YAxis stroke="#71717a" fontSize={12} tickFormatter={(value) => `$${value}`} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#18181b',
                        border: '1px solid #27272a',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value) => [`$${value}`, 'MRR']}
                    />
                    <Bar dataKey="mrr" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* User Management */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="user-management-card">
          <CardHeader>
            <CardTitle className="text-white">User Management</CardTitle>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <Input
                  placeholder="Search by name, email, or username..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  data-testid="user-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px] bg-zinc-800 border-zinc-700 text-white" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all" className="text-white">All Users</SelectItem>
                  <SelectItem value="active" className="text-white">Active Members</SelectItem>
                  <SelectItem value="free" className="text-white">Free Users</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                onClick={handleSearch}
                className="bg-amber-500 hover:bg-amber-600 text-black"
                data-testid="search-button"
              >
                Search
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-zinc-400 mb-4">
              Showing {users.length} of {usersTotal} users
            </div>
            <div className="rounded-lg border border-zinc-800 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400">User</TableHead>
                    <TableHead className="text-zinc-400">Email</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400">Joined</TableHead>
                    <TableHead className="text-zinc-400 text-right">Sales</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-zinc-500 py-8">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((u) => (
                      <TableRow key={u.id} className="border-zinc-800 hover:bg-zinc-800/50" data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium text-white">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-zinc-700 flex items-center justify-center text-sm font-medium text-white">
                              {u.name?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div>
                              <div className="font-medium">{u.name}</div>
                              <div className="text-xs text-zinc-500">@{u.username}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-zinc-300">{u.email}</TableCell>
                        <TableCell>{getStatusBadge(u.status_label)}</TableCell>
                        <TableCell className="text-zinc-400">{formatDate(u.created_at)}</TableCell>
                        <TableCell className="text-right text-zinc-300">{u.total_sales || 0}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
